import { Controller } from "@hotwired/stimulus"
import { showError } from '../lib/platform_toast'

/**
 * Listing Wizard Controller
 * 6-step wizard for real estate listing creation
 *
 * UX Psychology Applied:
 *   - Goal Gradient Effect: Progress bar with "almost done!" message
 *   - Hick's Law: Limited options per field
 *   - Fitts' Law: Large touch targets for buttons
 */
export default class extends Controller {
  static targets = [
    "progressBar",
    "priceLabel",
    "priceUnit",
    "depositField",
    "depositRequired",
    "prevButton",
    "nextButton",
    "submitButton",
    "listingTypeRadio",
    "addressField",
    "latitudeField",
    "longitudeField",
    "businessAddressField"
  ]

  static values = {
    currentStep: Number,
    listingId: Number,
    businessConstants: Object,
    imagesMinError: String
  }

  connect() {
    // Initialize listing type watchers
    this.initListingTypeWatcher()
  }

  /**
   * Watch listing type changes to toggle deposit field visibility
   */
  initListingTypeWatcher() {
    if (!this.hasListingTypeRadioTarget) return

    this.listingTypeRadioTargets.forEach(radio => {
      radio.addEventListener("change", this.handleListingTypeChange.bind(this))
    })
  }

  /**
   * Handle listing type change (rent/sale)
   * Shows/hides deposit field based on selection
   */
  handleListingTypeChange(event) {
    const isRent = event.target.value === "rent"

    if (this.hasDepositFieldTarget) {
      this.depositFieldTarget.classList.toggle("hidden", !isRent)
    }

    // Update price labels (I18n: read from data attributes)
    if (this.hasPriceLabelTarget) {
      this.priceLabelTarget.textContent = isRent
        ? this.priceLabelTarget.dataset.rentLabel
        : this.priceLabelTarget.dataset.saleLabel
    }

    if (this.hasPriceUnitTarget) {
      this.priceUnitTarget.textContent = isRent
        ? this.priceUnitTarget.dataset.rentUnit
        : this.priceUnitTarget.dataset.saleUnit
    }

    // Toggle deposit required indicator
    if (this.hasDepositRequiredTarget) {
      this.depositRequiredTarget.classList.toggle("hidden", !isRent)
    }
  }

  /**
   * Set deposit based on months of rent
   * Called from deposit quick-select buttons
   */
  setDepositMonths(event) {
    const months = parseInt(event.currentTarget.dataset.months, 10)
    const priceInput = this.element.querySelector('[name="listing[price]"]')
    const depositInput = this.element.querySelector('[name="listing[deposit]"]')

    if (priceInput && depositInput) {
      const price = parseInt(priceInput.value, 10) || 0
      depositInput.value = price * months
    }
  }

  /**
   * Get minimum listing images from business constants
   */
  get minListingImages() {
    return this.businessConstantsValue?.min_listing_images || 1
  }

  /**
   * Get maximum listing images from business constants
   */
  get maxListingImages() {
    return this.businessConstantsValue?.max_listing_images || 10
  }

  /**
   * Handle property address selection from the address autocomplete (Google Places OR self-hosted
   * gazetteer — both dispatch the same {latitude, longitude, address} detail).
   * Updates hidden latitude/longitude fields and moves the interactive map-picker pin.
   */
  handleAddressSelected(event) {
    const { latitude, longitude, address } = event.detail

    // Update hidden fields (property GPS coordinates)
    if (this.hasLatitudeFieldTarget) {
      this.latitudeFieldTarget.value = latitude
    }
    if (this.hasLongitudeFieldTarget) {
      this.longitudeFieldTarget.value = longitude
    }

    // Update address field with formatted address
    if (this.hasAddressFieldTarget) {
      this.addressFieldTarget.value = address
    }

    // Move the interactive map-picker pin to the selected address.
    this.updateMapPicker(latitude, longitude, address)
  }

  /**
   * The user dragged/tapped the map-picker pin. The shared hidden lat/lng fields are ALREADY updated by the
   * map-picker (they are its Stimulus targets too), so GPS stays correct automatically; we only keep the
   * VISIBLE address honest. The Google path carries the address on this event; the self-hosted path resolves
   * it a moment later via map-picker:address-resolved (handled below). Wired by data-action in _step5.
   */
  handleMapCoordinateChange(event) {
    const { address } = event.detail
    if (address && this.hasAddressFieldTarget) {
      this.addressFieldTarget.value = address
    }
  }

  /**
   * Map-picker finished reverse-geocoding the dragged pin → sync the visible address to the new location so
   * the pin, the saved coordinates, and the address string never diverge. Non-destructive: stays editable.
   */
  handleAddressResolved(event) {
    const { address } = event.detail
    if (address && this.hasAddressFieldTarget) {
      this.addressFieldTarget.value = address
    }
  }

  /**
   * Handle business address selection from Google Places Autocomplete
   * Only updates the address field (no GPS needed for 經紀業 office location)
   */
  handleBusinessAddressSelected(event) {
    const { address } = event.detail

    if (this.hasBusinessAddressFieldTarget) {
      this.businessAddressFieldTarget.value = address
    }
  }

  /**
   * Validate Step 5 before form submission
   * Checks minimum photo count (existing + newly uploaded)
   */
  validateBeforeSubmit(event) {
    const previewContainer = this.element.querySelector('[data-direct-upload-target="preview"]')
    const imageCount = previewContainer?.querySelectorAll('.direct-upload-preview').length || 0

    if (imageCount < this.minListingImages) {
      event.preventDefault()
      showError(this.imagesMinErrorValue)
    }
  }

  /**
   * Move the interactive map-picker pin + recenter the map when the ADDRESS field changes (autocomplete).
   * The reverse flow (pin drag → address) is handled by handleMapCoordinateChange / handleAddressResolved.
   * Mirrors product-form/location.js#updateMapPickerMarker: locate the sibling map-picker controller and
   * call its public updateMarker() (which moves the pin, pans the map, writes the hidden lat/lng fields, and
   * dispatches map-picker:coordinate-changed) — works for BOTH the self-hosted MapLibre and Google engines.
   * No-ops gracefully when no map-picker is mounted (e.g. the no-map fallback when self-hosted is off + no key).
   */
  updateMapPicker(latitude, longitude, address = null) {
    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)
    if (Number.isNaN(lat) || Number.isNaN(lng)) return

    const el = this.element.querySelector("[data-controller~='map-picker']")
    if (!el) return

    const picker = this.application.getControllerForElementAndIdentifier(el, "map-picker")
    picker?.updateMarker?.(lat, lng, address)
  }
}
